<script setup lang="ts">
import { ref, watch } from 'vue';
import { BTextarea } from '@/design-system';
import { useJson } from '@/composables';
import type { VTextarea } from 'vuetify/components';

type PropsType = {
    modelValue?: any;
    label?: string;
    placeholder?: string;
    variant?: VTextarea['$props']['variant'];
    density?: VTextarea['$props']['density'];
    color?: string;
    clearable?: boolean;
    loading?: boolean;
    isLabelTranslated?: boolean;
    showRequiredVisual?: boolean;
    fieldName?: string;
    panelIndex?: number;
    tabId?: string;
    rows?: number;
    autoGrow?: boolean;
    hideDetails?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    variant: 'outlined',
    density: 'compact',
    clearable: false,
    loading: false,
    isLabelTranslated: true,
    showRequiredVisual: true,
    placeholder: '{"key": "value"}',
    rows: 4,
    autoGrow: true,
    hideDetails: false,
});

const emit = defineEmits<{
    'update:modelValue': [value: any];
    blur: [event: FocusEvent];
    focus: [event: FocusEvent];
}>();

const { jsonValidator, formatJsonForDisplay, parseJsonSafely } = useJson();
const localJsonString = ref<string>('{}');

watch(
    () => props.modelValue,
    newValue => {
        if (typeof newValue === 'string') {
            localJsonString.value = newValue;
        } else if (newValue !== null && newValue !== undefined) {
            localJsonString.value = formatJsonForDisplay(newValue);
        } else {
            localJsonString.value = '{}';
        }
    },
    { immediate: true },
);

const handleStringChange = (jsonString: string) => {
    localJsonString.value = jsonString;

    const parseResult = parseJsonSafely(jsonString);
    if (parseResult.success) {
        emit('update:modelValue', parseResult.data);
    } else {
        emit('update:modelValue', jsonString);
    }
};
</script>

<template>
    <BTextarea
        :model-value="localJsonString"
        :label="label"
        :placeholder="placeholder"
        :variant="variant"
        :density="density"
        :color="color"
        :clearable="clearable"
        :loading="loading"
        :is-label-translated="isLabelTranslated"
        :show-required-visual="showRequiredVisual"
        :field-name="fieldName"
        :panel-index="panelIndex"
        :tab-id="tabId"
        :rows="rows"
        :auto-grow="autoGrow"
        :hide-details="hideDetails"
        :rules="[jsonValidator]"
        @update:model-value="handleStringChange"
        @blur="emit('blur', $event)"
        @focus="emit('focus', $event)"
    />
</template>
